/**
 * WebPlacementSelectorDialog Component
 *
 * Step 1 of UnifiedWebPlacementFlow:
 * - Shows "Create New Web Funnel" option at top
 * - Lists existing web placements below (if any)
 * - If no placements exist, shows only "Create New" with helpful message
 *
 * @example
 * ```tsx
 * <WebPlacementSelectorDialog
 *   open={true}
 *   onClose={() => setOpen(false)}
 *   projectId="proj-123"
 *   onSelect={(selection) => {
 *     if (selection.type === 'create-new') {
 *       // Open create dialog
 *     } else {
 *       // Edit existing placement
 *       console.log('Selected:', selection.webPlacement.id);
 *     }
 *   }}
 * />
 * ```
 *
 * @module webplacements/components
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTheme } from '@mui/material/styles';
import { SDialog } from '@modules/ui-components';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import type { WebPlacement } from '@modules/webplacements/types/webplacements.types';

/**
 * Selection result type
 */
export type WebPlacementSelectorResult =
  | { type: 'create-new' }
  | { type: 'existing'; webPlacement: WebPlacement };

/**
 * Props for WebPlacementSelectorDialog
 */
export interface WebPlacementSelectorDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog closes */
  onClose: () => void;
  /** Project ID to load web placements from */
  projectId: string;
  /** Optional: ID of the onboarding being deployed. Used to prioritize matching placements. */
  onboardingId?: string;
  /** Callback when selection is made */
  onSelect: (selection: WebPlacementSelectorResult) => void;
  /** Optional: Callback to duplicate a placement (copies settings, clears onboarding) */
  onDuplicate?: (sourceWp: WebPlacement) => void;
  /** Optional: Callback to full-copy a placement (preserves funnel/variable refs) */
  onFullCopy?: (sourceWp: WebPlacement) => void;
}

/**
 * WebPlacementSelectorDialog Component
 */
export const WebPlacementSelectorDialog: React.FC<WebPlacementSelectorDialogProps> = ({
  open,
  onClose,
  projectId,
  onboardingId,
  onSelect,
  onDuplicate,
  onFullCopy,
}) => {
  const theme = useTheme();

  // State
  const [webPlacements, setWebPlacements] = useState<WebPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Placement ID (empty string = nothing selected)
  const [selectedValue, setSelectedValue] = useState<string>('');
  // Search query — filters placements by name OR alias (case-insensitive, trimmed)
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Service
  const webPlacementService = useMemo(() => new WebPlacementService(), []);

  // Sort placements: matching onboardingId first (by publishedAt desc), then rest
  const sortedPlacements = useMemo(() => {
    if (!onboardingId || webPlacements.length === 0) return webPlacements;

    const matching: WebPlacement[] = [];
    const rest: WebPlacement[] = [];

    for (const wp of webPlacements) {
      if (wp.config?.funnelId === onboardingId) {
        matching.push(wp);
      } else {
        rest.push(wp);
      }
    }

    // Sort matching by publishedAt descending (most recent first)
    matching.sort((a, b) => {
      const aDate = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const bDate = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return bDate - aDate;
    });

    return [...matching, ...rest];
  }, [webPlacements, onboardingId]);

  // Apply search filter on top of the existing sort (matching→rest order is preserved)
  const filteredPlacements = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return sortedPlacements;
    return sortedPlacements.filter(
      (wp) =>
        wp.name.toLowerCase().includes(query) ||
        wp.alias.toLowerCase().includes(query)
    );
  }, [sortedPlacements, searchQuery]);

  // Load web placements when dialog opens
  useEffect(() => {
    if (open && projectId) {
      setLoading(true);
      setError(null);

      webPlacementService
        .list(projectId)
        // Onboarding-funnel picker: article-backed placements are not
        // selectable here (they carry an article draft, not a funnel).
        .then((all) => all.filter((wp) => wp.sourceKind !== 'article'))
        .then((data) => {
          setWebPlacements(data);

          // Auto-select the matching placement with most recent publishedAt
          if (onboardingId) {
            const matchingPlacements = data
              .filter((wp) => wp.config?.funnelId === onboardingId)
              .sort((a, b) => {
                const aDate = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
                const bDate = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
                return bDate - aDate;
              });

            if (matchingPlacements.length > 0) {
              setSelectedValue(matchingPlacements[0].id);
            }
          }

          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load web placements:', err);
          setError(err instanceof Error ? err.message : 'Failed to load web placements');
          setLoading(false);
        });
    }
  }, [open, projectId, onboardingId, webPlacementService]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedValue('');
      setError(null);
      setSearchQuery('');
    }
  }, [open]);

  // Get selected web placement
  const selectedWebPlacement = useMemo(
    () => (selectedValue ? webPlacements.find((wp) => wp.id === selectedValue) || null : null),
    [webPlacements, selectedValue]
  );

  /**
   * Handle "Create New" button click - directly opens creation dialog
   */
  const handleCreateNew = useCallback(() => {
    onSelect({ type: 'create-new' });
  }, [onSelect]);

  /**
   * Handle Next button click - only for existing placements
   */
  const handleNext = useCallback(() => {
    if (selectedWebPlacement) {
      onSelect({ type: 'existing', webPlacement: selectedWebPlacement });
    }
  }, [selectedWebPlacement, onSelect]);

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  }, []);

  // Check if form is valid - only when existing placement is selected
  const canProceed = selectedWebPlacement !== null;

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title="Deploy to Web Funnel"
      maxWidth="sm"
      fullWidth
      data-testid="webpl-selector-dialog"
      primaryAction={{
        label: 'Next',
        onClick: handleNext,
        disabled: !canProceed,
        'data-testid': 'webpl-selector-next-btn',
        sx: canProceed
          ? {
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'shimmer 3s ease-in-out infinite',
              },
              '@keyframes shimmer': {
                '0%': { left: '-100%' },
                '50%': { left: '100%' },
                '100%': { left: '100%' },
              },
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(3),
        }}
      >
        {/* Error Message */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Instructions */}
            <Typography variant="body2" color="text.secondary">
              Select an existing web funnel to update, or create a new one:
            </Typography>

            {/* Create New Button — right-aligned, content-width (not full-width) */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              data-testid="webpl-selector-create-new-btn"
              sx={{
                alignSelf: 'flex-end',
                borderStyle: 'dashed',
              }}
            >
              Create New Web Funnel
            </Button>

            {/* Search Field — filters existing placements by name OR alias */}
            {webPlacements.length > 0 && (
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or alias..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputProps={{ 'data-testid': 'webpl-selector-search' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        data-testid="webpl-selector-search-clear"
                        aria-label="Clear search"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}

            {/* Selection List — fixed vertical footprint (single height, not a min/max range)
                so the dialog has a stable height regardless of list length or filter state.
                Long lists scroll internally; short lists / empty-filter state render inside
                the same reserved area. */}
            <Box
              sx={{
                height: '50vh',
                overflowY: 'auto',
              }}
            >
              <RadioGroup value={selectedValue} onChange={handleSelectionChange}>
                {/* Existing Web Placements */}
                {filteredPlacements.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                      Existing Web Funnels
                    </Typography>
                    {filteredPlacements.map((webPlacement) => {
                      const isMatch = !!(onboardingId && webPlacement.config?.funnelId === onboardingId);
                      return (
                        <Box
                          key={webPlacement.id}
                          data-testid={`webpl-selector-item-${webPlacement.id}`}
                          sx={{
                            mb: 1,
                            p: 1.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: selectedValue === webPlacement.id
                              ? 'primary.main'
                              : isMatch
                                ? 'info.main'
                                : 'divider',
                            bgcolor: selectedValue === webPlacement.id ? 'action.selected' : 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <FormControlLabel
                            value={webPlacement.id}
                            control={<Radio />}
                            label={
                              <Box sx={{ width: '100%' }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 0.5,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <Typography variant="body1" fontWeight="medium">
                                    {webPlacement.name}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={webPlacement.status === 'published' ? 'Published' : 'Draft'}
                                    color={webPlacement.status === 'published' ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                  {isMatch && (
                                    <Chip
                                      size="small"
                                      label="Previously deployed"
                                      color="info"
                                      variant="filled"
                                    />
                                  )}
                                  {onDuplicate && (
                                    <Tooltip title="Duplicate">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          onDuplicate(webPlacement);
                                        }}
                                        data-testid={`webpl-selector-duplicate-${webPlacement.id}`}
                                        sx={{ ml: 'auto' }}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {onFullCopy && (
                                    <Tooltip title="Full Copy">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          onFullCopy(webPlacement);
                                        }}
                                        data-testid={`webpl-selector-full-copy-${webPlacement.id}`}
                                      >
                                        <FileCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  /{webPlacement.alias}
                                </Typography>
                                {webPlacement.config?.entityName && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Current: {webPlacement.config.entityName}
                                  </Typography>
                                )}
                                {webPlacement.updatedAt && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Updated: {new Date(webPlacement.updatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Box>
                      );
                    })}
                  </>
                )}

                {/* No existing placements message — shown ONLY when there are none at all */}
                {webPlacements.length === 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No existing web funnels found. Create a new one to get started.
                  </Alert>
                )}

                {/* Zero-filter-results — shown when placements exist but query filters them all out */}
                {webPlacements.length > 0 && filteredPlacements.length === 0 && (
                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                    data-testid="webpl-selector-empty-filter"
                  >
                    No web funnels match "{searchQuery}"
                  </Alert>
                )}
              </RadioGroup>
            </Box>

            {/* Warning when updating existing placement */}
            {selectedWebPlacement && selectedWebPlacement.config?.funnelId && (
              <Alert severity="info">
                <Typography variant="body2" fontWeight="medium">
                  Existing onboarding will be replaced
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  The current onboarding configuration will be updated with the new onboarding from the canvas.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </Box>
    </SDialog>
  );
};

export default WebPlacementSelectorDialog;
